import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import TokenManager from '../../../utils/storage/TokenManager';
import { AIChatAgentIcons } from '../../helpers/iconHelpers';
import './AIChatAgent.sass';

const {
	AIIcon, HeaderIcon, SendIcon, NewChatIcon,
	SparkleIcon, BulbIcon, QuestionIcon, ClockIcon, UserIcon,
} = AIChatAgentIcons;

const formatTime = (time) => {
	if (!time) return '';
	const m = moment(time);

	return m.isValid() ? m.format('HH:mm') : '';
};

const formatRelativeTime = (time, t) => {
	if (!time) return '';
	const m = moment(time);
	if (!m.isValid()) return '';
	const diffMin = moment().diff(m, 'minutes');
	if (diffMin < 1) return t('justNow');
	if (diffMin < 60) return t('minutesAgo', { count: diffMin });
	const diffHours = moment().diff(m, 'hours');
	if (diffHours < 24) return t('hoursAgo', { count: diffHours });

	return m.format('L');
};

const AIChatAgent = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'aiChatAgent' });

	const [isOpen, setIsOpen] = useState(false);
	const [conversations, setConversations] = useState([]);
	const [activeConvId, setActiveConvId] = useState(null);
	const [input, setInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [historyLoaded, setHistoryLoaded] = useState(false);
	const messagesEndRef = useRef(null);

	const SUGGESTIONS = [
		{ icon: SparkleIcon, title: t('suggestionAskTitle'), description: t('suggestionAskDesc') },
		{ icon: NewChatIcon, title: t('suggestionDeviceTitle'), description: t('suggestionDeviceDesc') },
		{ icon: BulbIcon, title: t('suggestionReportTitle'), description: t('suggestionReportDesc') },
		{ icon: QuestionIcon, title: t('suggestionHelpTitle'), description: t('suggestionHelpDesc') },
	];

	const activeConversation = conversations.find(c => c.id === activeConvId);
	const messages = activeConversation?.messages || [];

	useEffect(() => {
		if (isOpen && !historyLoaded) loadHistory();
	}, [isOpen]);

	useEffect(() => {
		if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	const createNewConversation = () => {
		const id = `conv-${moment().valueOf()}`;
		const newConv = {
			id,
			title: t('newConversation'),
			preview: t('newConversationPreview'),
			time: moment(),
			messages: [],
		};
		setConversations(prev => [newConv, ...prev]);
		setActiveConvId(id);

		return id;
	};

	const loadHistory = () => {
		TokenManager.getAccessToken()
			.then(token => fetch('/ai-proxy/api/history?limit=50', {
				headers: { Authorization: `Bearer ${token}` },
			}))
			.then(res => res.json())
			.then((data) => {
				const history = data?.history || data || [];
				const formatted = history.reverse().flatMap(item => {
					const msgs = [];
					if (item.user_message || item.message)
						msgs.push({ role: 'user', text: item.user_message || item.message, time: moment(item.created_at) });
					if (item.ai_response || item.response)
						msgs.push({ role: 'ai', text: item.ai_response || item.response, time: moment(item.created_at) });
					return msgs;
				});

				if (formatted.length > 0) {
					setConversations([{
						id: 'history',
						title: formatted.find(m => m.role === 'user')?.text || t('previousConversation'),
						preview: formatted.filter(m => m.role === 'ai').slice(-1)[0]?.text || '',
						time: formatted.slice(-1)[0]?.time || moment(),
						messages: formatted,
					}]);
					setActiveConvId('history');
				} else {
					createNewConversation();
				}
				setHistoryLoaded(true);
			})
			.catch((err) => {
				console.error('AI history error:', err);
				setHistoryLoaded(true);
				createNewConversation();
			});
	};

	const sendMessage = (text) => {
		const messageText = text || input.trim();
		if (!messageText || isLoading) return;

		setInput('');
		const now = moment();
		const targetConvId = activeConvId || createNewConversation();
		const userMsg = { role: 'user', text: messageText, time: now };

		setConversations(prev => prev.map(c => {
			if (c.id !== targetConvId) return c;

			return {
				...c,
				title: c.messages.length === 0 ? messageText : c.title,
				preview: messageText,
				time: now,
				messages: [...c.messages, userMsg],
			};
		}));

		setIsLoading(true);

		TokenManager.getAccessToken()
			.then(token => fetch('/ai-proxy/api/chat', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
				body: JSON.stringify({ message: messageText }),
			}))
			.then(res => res.json())
			.then((data) => {
				const reply = data?.response || data?.message || '';
				const aiMsg = { role: 'ai', text: reply, time: moment() };
				setConversations(prev => prev.map(c =>
					c.id !== targetConvId ? c : { ...c, preview: reply, time: moment(), messages: [...c.messages, aiMsg] }
				));
			})
			.catch((err) => {
				console.error('AI chat error:', err);
				const errMsg = { role: 'ai', text: t('errorMessage'), time: moment() };
				setConversations(prev => prev.map(c =>
					c.id !== targetConvId ? c : { ...c, messages: [...c.messages, errMsg] }
				));
			})
			.finally(() => setIsLoading(false));
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	return (
		<div className="ai-chat-agent">
			{isOpen && (
				<div className="popup">
					<div className="sidebar">
						<button className="new-chat-btn" onClick={createNewConversation}>
							<NewChatIcon />
							{t('newChat')}
						</button>
						<div className="conv-list">
							{conversations.map(conv => (
								<div
									key={conv.id}
									className={`conv-item${conv.id === activeConvId ? ' conv-item--active' : ''}`}
									onClick={() => setActiveConvId(conv.id)}
								>
									<div className="conv-icon"><NewChatIcon /></div>
									<div className="conv-info">
										<div className="conv-title">{conv.title}</div>
										<div className="conv-preview">{conv.preview}</div>
										<div className="conv-meta">
											<span className="conv-time">
												<ClockIcon />
												{formatRelativeTime(conv.time, t)}
											</span>
											{conv.messages.length > 0 && (
												<span className="conv-count">
													&bull; {t('messages', { count: conv.messages.length })}
												</span>
											)}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="main">
						<div className="header">
							<div className="header-left">
								<div className="header-icon-wrap"><HeaderIcon className="header-icon" /></div>
								<div className="header-info">
									<span className="header-title">{t('headerTitle')}</span>
									<span className="header-status">{t('headerStatus')}</span>
								</div>
							</div>
							<button className="close" onClick={() => setIsOpen(false)} aria-label="Close">&#x2715;</button>
						</div>

						<div className="messages">
							{messages.length === 0 && !isLoading ? (
								<div className="welcome">
									<h2 className="welcome-title">{t('welcomeTitle')}</h2>
									<p className="welcome-subtitle">{t('welcomeSubtitle')}</p>
									<div className="suggestions-grid">
										{SUGGESTIONS.map((s, i) => {
											const Icon = s.icon;
											return (
												<button
													key={i}
													className="suggestion-card"
													onClick={() => sendMessage(s.description)}
												>
													<span className="card-icon"><Icon /></span>
													<span className="card-text">
														<span className="card-title">{s.title}</span>
														<p className="card-desc">{s.description}</p>
													</span>
												</button>
											);
										})}
									</div>
								</div>
							) : (
								<>
									{messages.map((msg, i) => (
										<div key={i} className={`message message--${msg.role}`}>
											<div className={`avatar avatar--${msg.role}`}>
												{msg.role === 'ai'
													? <HeaderIcon width="16" height="16" className="msg-icon" />
													: <UserIcon className="msg-icon" />
												}
											</div>
											<div className="content">
												<div className="msg-bubble">{msg.text}</div>
												<div className="msg-time">{formatTime(msg.time)}</div>
											</div>
										</div>
									))}
									{isLoading && (
										<div className="message message--ai">
											<div className="avatar avatar--ai"><HeaderIcon width="16" height="16" className="msg-icon" /></div>
											<div className="content">
												<div className="msg-bubble msg-bubble--typing"><span /><span /><span /></div>
											</div>
										</div>
									)}
								</>
							)}
							<div ref={messagesEndRef} />
						</div>

						<div className="input-row">
							<textarea
								className="input"
								placeholder={t('placeholder')}
								value={input}
								onChange={e => setInput(e.target.value)}
								onKeyDown={handleKeyDown}
								rows={1}
								disabled={isLoading}
							/>
							<button
								className="send"
								onClick={() => sendMessage()}
								disabled={!input.trim() || isLoading}
								aria-label="Send"
							>
								<SendIcon />
							</button>
						</div>
					</div>
				</div>
			)}

			<div className="border-wrap" onClick={() => setIsOpen(o => !o)}>
				<button className="btn">
					<AIIcon />
					<span className="label">{t('label')}</span>
				</button>
			</div>
		</div>
	);
};

export default AIChatAgent;
